import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Mail, Phone, Calendar, CheckCircle2, Eye } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/contact-submissions`);
      setSubmissions(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`${BACKEND_URL}/api/cms/contact-submissions/${id}`, {
        status: 'read'
      });
      fetchSubmissions();
    } catch (error) {
      console.error('Error updating submission:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-3xl font-light text-[#2d2d2d]">Contact Form Submissions</h1>
        <p className="text-[#2d2d2d] mt-2">
          Total submissions: {submissions.length} | 
          New: {submissions.filter(s => s.status === 'new').length}
        </p>
      </div>

      <div className="space-y-4">
        {submissions.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow text-center">
            <p className="text-[#2d2d2d]">No submissions yet.</p>
          </div>
        ) : (
          submissions.map((submission) => (
            <div
              key={submission.id}
              className={`bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow ${
                submission.status === 'new' ? 'border-l-4 border-[#0A1A50]' : ''
              }`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-xl font-normal text-[#2d2d2d]">{submission.name}</h3>
                    {submission.status === 'new' && (
                      <span className="px-3 py-1 bg-[#0A1A50] text-white text-xs rounded-full">
                        NEW
                      </span>
                    )}
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-[#2d2d2d]">
                      <Mail className="w-4 h-4" />
                      <a href={`mailto:${submission.email}`} className="hover:text-[#0A1A50]">
                        {submission.email}
                      </a>
                    </div>
                    
                    {submission.phone && (
                      <div className="flex items-center gap-2 text-[#2d2d2d]">
                        <Phone className="w-4 h-4" />
                        <a href={`tel:${submission.phone}`} className="hover:text-[#0A1A50]">
                          {submission.phone}
                        </a>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2 text-[#2d2d2d] text-sm">
                      <Calendar className="w-4 h-4" />
                      {formatDate(submission.submitted_at)}
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-[#2d2d2d] font-light whitespace-pre-wrap">
                      {submission.message}
                    </p>
                  </div>
                </div>

                <div className="ml-4 flex flex-col gap-2">
                  {submission.status === 'new' && (
                    <Button
                      onClick={() => markAsRead(submission.id)}
                      size="sm"
                      variant="outline"
                      className="text-[#0A1A50] border-[#0A1A50]"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Mark as Read
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ContactSubmissions;
